import { useEffect, useState } from 'react';
import type { PendingSendView } from './pendingSend';

/**
 * Whole seconds left on a pending send, ticking.
 *
 * Read from the deadline on every tick rather than decremented, so a throttled
 * background timer cannot make the number drift from the clock that actually
 * commits the send. While the clock is held the number stands still.
 */
export function useSendSecondsLeft(pending: PendingSendView | undefined): number | undefined {
  const deadline = pending?.undoable ? pending.deadline : undefined;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (deadline === undefined) return undefined;
    const tick = () => setNow(Date.now());
    const first = setTimeout(tick, 0);
    const timer = setInterval(tick, 250);
    return () => {
      clearTimeout(first);
      clearInterval(timer);
    };
  }, [deadline]);
  if (!pending?.undoable) return undefined;
  if (pending.heldSeconds !== undefined) return pending.heldSeconds;
  if (deadline === undefined) return undefined;
  // Clamped to the window: until the first tick, `now` may be from long ago.
  const whole = pending.windowSeconds ?? Number.POSITIVE_INFINITY;
  return Math.min(whole, Math.max(0, Math.ceil((deadline - now) / 1000)));
}

/**
 * "12 s", beside "Sending…". Hidden from assistive technology: the snackbar is
 * a polite live region, and a number that changes every second would be read
 * out every second. The sentence is announced once; the count is for the eye.
 */
export function SendCountdown({ pending }: { pending: PendingSendView | undefined }) {
  const left = useSendSecondsLeft(pending);
  if (left === undefined) return null;
  return (
    <span className="send-countdown" aria-hidden="true" data-seconds={left}>
      {`${left} s`}
    </span>
  );
}
